import { AuditAction, AuditEntity, AuditLogDoc } from '@gooduncles/gu-app-schema';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import 'ag-grid-enterprise';
import { ColDef } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { Select, Tag } from 'antd';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { AUDIT_LOG_EMAILS } from 'src/routes/logRoutes';
import { formatDateForAgCell, loadGridState } from 'src/utils/ag-grid-util';
import { AuditLogger } from 'src/utils/audit-logger';
import { RangeEventValue, dayjsesToDateWhere, getDefaultDates } from 'src/utils/date-util';
import { FirebaseManager } from 'src/utils/firebase/firebase-manager';

import { useTitle } from 'src/hooks/useTitle';

import { useAuth } from 'src/stores/auth-context';

import DownloadRawDataCell from 'src/atomic-components/atoms/AgCell/AgCellDownloadRawData';
import AgGridSettingsButtons from 'src/atomic-components/molecules/AgGridSettingsButtons/AgGridSettingsButtons';
import TableOptions from 'src/components/Common/TableOptions/TableOptions';

const firebaseManager = FirebaseManager.getInstance();
const auditLogger = AuditLogger.getInstance();
const auditLogPath = 'auditLog';
const TABLE_ID = 'auditLogTable';
const defaultDates = getDefaultDates();

const actionColorMap: Record<AuditAction, string> = {
  CREATE: 'green',
  UPDATE: 'blue',
  DELETE: 'red',
  STATUS_CHANGE: 'orange',
  BULK_UPDATE: 'purple',
  SEND: 'cyan',
  SYNC: 'geekblue',
};

const entityColorMap: Record<AuditEntity, string> = {
  order: 'volcano',
  product: 'green',
  store: 'blue',
  inventory: 'purple',
  issue: 'orange',
  issueTask: 'gold',
  settlement: 'magenta',
  delivery: 'cyan',
  user: 'geekblue',
  config: 'lime',
  promotion: 'pink',
  notification: 'default',
  purchaseOrder: 'red',
  storeCategory: 'green',
  session: 'default',
  misc: 'default',
};

const entityOptions: { label: string; value: AuditEntity }[] = [
  { label: '주문', value: 'order' },
  { label: '상품', value: 'product' },
  { label: '매장', value: 'store' },
  { label: '재고', value: 'inventory' },
  { label: '이슈', value: 'issue' },
  { label: '이슈 태스크', value: 'issueTask' },
  { label: '정산', value: 'settlement' },
  { label: '배송', value: 'delivery' },
  { label: '사용자', value: 'user' },
  { label: '설정', value: 'config' },
  { label: '프로모션', value: 'promotion' },
  { label: '알림', value: 'notification' },
  { label: '발주', value: 'purchaseOrder' },
  { label: '매장 카테고리', value: 'storeCategory' },
  { label: '세션', value: 'session' },
  { label: '기타', value: 'misc' },
];

const actionOptions: { label: string; value: AuditAction }[] = [
  { label: '생성', value: 'CREATE' },
  { label: '수정', value: 'UPDATE' },
  { label: '삭제', value: 'DELETE' },
  { label: '상태 변경', value: 'STATUS_CHANGE' },
  { label: '일괄 수정', value: 'BULK_UPDATE' },
  { label: '발송', value: 'SEND' },
  { label: '동기화', value: 'SYNC' },
];

const levelOptions = [
  { label: '전체', value: '' },
  { label: 'log', value: 'log' },
  { label: 'warn', value: 'warn' },
  { label: 'error', value: 'error' },
];

const defaultColDef: ColDef<AuditLogDoc> = {
  sortable: true,
  resizable: true,
  filter: true,
};

const ActionCellRenderer: FC<{ value: AuditAction }> = ({ value }) => {
  if (!value) return null;
  return <Tag color={actionColorMap[value]}>{value}</Tag>;
};

const EntityCellRenderer: FC<{ value: AuditEntity }> = ({ value }) => {
  if (!value) return null;
  const option = entityOptions.find((o) => o.value === value);
  return <Tag color={entityColorMap[value]}>{option?.label ?? value}</Tag>;
};

const LevelCellRenderer: FC<{ value: string }> = ({ value }) => {
  if (!value) return null;
  const colorMap: Record<string, string> = { log: 'default', warn: 'warning', error: 'error', debug: 'processing' };
  return <Tag color={colorMap[value] ?? 'default'}>{value}</Tag>;
};

const ChangesCellRenderer: FC<{ value: AuditLogDoc['changes'] }> = ({ value }) => {
  if (!value || value.length === 0) return null;
  return (
    <span>
      {value.map((c, i) => (
        <span key={i}>
          <strong>{c.field}</strong>: {JSON.stringify(c.before)} → {JSON.stringify(c.after)}
          {i < value.length - 1 ? ', ' : ''}
        </span>
      ))}
    </span>
  );
};

interface AuditLogPageProps {
  title: string;
}

const AuditLogPage: FC<AuditLogPageProps> = ({ title }) => {
  useTitle(`GU 관리자 | ${title}`);
  const { user } = useAuth();
  const [gridRef, setGridRef] = useState<AgGridReact<AuditLogDoc> | null>(null);
  const [rowData, setRowData] = useState<AuditLogDoc[]>([]);
  const [dates, setDates] = useState<RangeEventValue>(null);
  const [selectedEntities, setSelectedEntities] = useState<AuditEntity[]>([]);
  const [selectedActions, setSelectedActions] = useState<AuditAction[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<string>('');

  const [leftDate, rightDate] = useMemo(() => {
    return dates?.[0] && dates?.[1]
      ? dayjsesToDateWhere(dates[0], dates[1])
      : dayjsesToDateWhere(defaultDates[0], defaultDates[1]);
  }, [dates]);

  const filteredData = useMemo(() => {
    return rowData
      .filter((row) => selectedEntities.length === 0 || selectedEntities.includes(row.entity))
      .filter((row) => selectedActions.length === 0 || selectedActions.includes(row.action))
      .filter((row) => !selectedLevel || row.level === selectedLevel);
  }, [rowData, selectedEntities, selectedActions, selectedLevel]);

  useEffect(() => {
    const subscription = firebaseManager
      .observeCollection<AuditLogDoc>(auditLogPath, [], {
        sortKey: '_timeCreate',
        orderBy: 'desc',
        startValue: leftDate,
        endValue: rightDate,
      })
      .subscribe((logs) => {
        setRowData(logs);
      });

    return () => {
      subscription.unsubscribe();
    };
  }, [leftDate, rightDate]);

  const columnDefs: ColDef<AuditLogDoc>[] = useMemo(
    () => [
      { field: '_id', hide: true },
      {
        headerName: '시각',
        field: '_timeCreate',
        width: 180,
        valueFormatter: formatDateForAgCell('yyyy-MM-dd HH:mm:ss'),
        sort: 'desc',
      },
      {
        headerName: '액션',
        field: 'action',
        width: 130,
        cellRenderer: ActionCellRenderer,
        enableRowGroup: true,
      },
      {
        headerName: '엔티티',
        field: 'entity',
        width: 120,
        cellRenderer: EntityCellRenderer,
        enableRowGroup: true,
      },
      {
        headerName: '레벨',
        field: 'level',
        width: 90,
        cellRenderer: LevelCellRenderer,
        enableRowGroup: true,
      },
      {
        headerName: '요약',
        field: 'summary',
        minWidth: 300,
        flex: 1,
        cellStyle: { whiteSpace: 'pre-wrap' },
        autoHeight: true,
      },
      {
        headerName: '엔티티 ID',
        field: 'entityId',
        width: 200,
        hide: true,
      },
      {
        headerName: '변경 내역',
        field: 'changes',
        minWidth: 300,
        cellRenderer: ChangesCellRenderer,
        autoHeight: true,
        hide: true,
      },
      {
        headerName: 'UID',
        field: 'uid',
        width: 240,
        enableRowGroup: true,
        hide: true,
      },
      {
        headerName: '경로',
        field: 'path',
        width: 200,
        hide: true,
      },
      {
        headerName: 'Session',
        field: 'sessionId',
        width: 300,
        enableRowGroup: true,
        hide: true,
      },
      {
        headerName: 'IP',
        field: 'publicIp',
        width: 140,
        hide: true,
      },
      {
        headerName: '액션',
        minWidth: 120,
        cellRenderer: DownloadRawDataCell,
        hide: true,
      },
    ],
    [],
  );

  const handleGridReady = useCallback(() => {
    if (gridRef) {
      loadGridState(gridRef, TABLE_ID);
    }
  }, [gridRef]);

  useEffect(() => {
    if (user?.email && !AUDIT_LOG_EMAILS.includes(user.email)) {
      auditLogger.log({
        action: 'SYNC',
        entity: 'session',
        entityId: user.email,
        summary: `감사 로그 페이지 비인가 접근 시도 - ${user.email}`,
        level: 'warn',
      });
    }
  }, [user?.email]);

  if (!user?.email || !AUDIT_LOG_EMAILS.includes(user.email)) {
    return <Navigate to='/dashboard' replace />;
  }

  return (
    <div className='height100 flexColumn'>
      <TableOptions setDates={setDates} defaultValue={defaultDates}>
        <Select
          mode='multiple'
          allowClear
          placeholder='엔티티 필터'
          style={{ minWidth: 200 }}
          options={entityOptions}
          value={selectedEntities}
          onChange={setSelectedEntities}
          maxTagCount='responsive'
        />
        <Select
          mode='multiple'
          allowClear
          placeholder='액션 필터'
          style={{ minWidth: 160 }}
          options={actionOptions}
          value={selectedActions}
          onChange={setSelectedActions}
          maxTagCount='responsive'
        />
        <Select
          allowClear
          placeholder='레벨'
          style={{ minWidth: 100 }}
          options={levelOptions}
          value={selectedLevel}
          onChange={(v) => setSelectedLevel(v ?? '')}
        />
        <AgGridSettingsButtons gridRef={gridRef} tableId={TABLE_ID} />
      </TableOptions>
      <div className='ag-theme-alpine height100'>
        <AgGridReact
          ref={setGridRef}
          rowData={filteredData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          animateRows={true}
          rowGroupPanelShow='always'
          onGridReady={handleGridReady}
          enableCellTextSelection
        />
      </div>
    </div>
  );
};

export default AuditLogPage;
